import React, {HTMLAttributes, useState, useEffect} from "react";
import classNames from 'classnames'
import {useNiceDateTime, NiceDateTimeMode} from "@webng/react-app-common";

interface DateTimeViewProp extends HTMLAttributes<HTMLSpanElement> {
  title?: string
  date?: string
  time?: string
  dateTime?: string,
  classNamesPrefix?: string
  children?: React.ReactNode
}


export function DateTimeView({date, time, dateTime, title, className, classNamesPrefix, children, ...attributes}: DateTimeViewProp) {
  // we need to use state here because of SSR (title got not updated on the client after hydration)
  const [tmpTitle, setTmpTitle] = useState<undefined|string>();

  useEffect(() => {
    if (title) {
      setTmpTitle(title);
    }
  }, [title]);


  const timestampClassNames = classNames(className, {
    [`${classNamesPrefix}ts`]: classNamesPrefix,
    'timestamp': !classNamesPrefix
  })
  const timestampDateClassNames = classNamesPrefix ? `${classNamesPrefix}ts__date` : 'timestamp__date';
  const timestampTimeClassNames = classNamesPrefix ? `${classNamesPrefix}ts__time` : 'timestamp__time';
  const timestampTimeValClassNames = classNamesPrefix ? `${classNamesPrefix}ts__time__val` : 'timestamp__time__val';

  // the time span always renders: it is the flex container that lays out the
  // children (icons, summary title), which must survive when no time value is
  // shown; the value spans only render when their value is defined — callers
  // must keep that presence stable across SSR/hydration (undefined vs. "")
  return <time suppressHydrationWarning={true} title={tmpTitle} dateTime={dateTime}
               className={timestampClassNames} {...attributes}>
    {date !== undefined && <React.Fragment>
      <span className={timestampDateClassNames} suppressHydrationWarning={true}>{date}</span>{' '}
    </React.Fragment>}
    <span className={timestampTimeClassNames}>
      {time !== undefined && <span className={timestampTimeValClassNames} suppressHydrationWarning={true}>{time}</span>}
      {children}
    </span>
  </time>;
}


export interface NiceDateTimeProps extends HTMLAttributes<HTMLSpanElement> {
  ts?: string|number|"now",
  skipRelative?: boolean,
  displayOverride?: string,
  mode?: NiceDateTimeMode,
  classNamesPrefix?: string,
  children?: React.ReactNode,
  onlyDate?: boolean
}

export function NiceDateTime({ts, skipRelative, displayOverride, mode = "smart", className, classNamesPrefix, onlyDate, children, ...attributes}: NiceDateTimeProps) {
  const {date, time, dateTime, isoString} = useNiceDateTime(ts, skipRelative, displayOverride, mode);

  // smart mode keeps both value spans in the DOM at all times (a relative
  // label upgrading to date + time between SSR and hydration must not change
  // the structure); the other modes have configuration-stable spans
  const dateValue = mode === "smart" ? (date ?? '') : date;
  const timeValue = onlyDate ? undefined : (mode === "smart" ? (time ?? '') : time);

  return <DateTimeView suppressHydrationWarning={true}
                       title={dateTime} dateTime={isoString} className={className}
                       classNamesPrefix={classNamesPrefix}
                       date={dateValue} time={timeValue} {...attributes}>
    {children}
  </DateTimeView>;
}

export interface NiceSportTimeProps extends HTMLAttributes<HTMLSpanElement> {

}
